import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  InputAdornment,
  Snackbar,
  TextField,
  Typography,
} from '@material-ui/core';
import MuiAlert from '@material-ui/lab/Alert';
import SaveIcon from '@material-ui/icons/Save';
import CloseIcon from '@material-ui/icons/Close';
import ExitToAppIcon from '@material-ui/icons/ExitToApp';
import Visibility from '@material-ui/icons/Visibility';
import VisibilityOff from '@material-ui/icons/VisibilityOff';
import { useSetRecoilState, useRecoilValue } from 'recoil';
import { useNavigate } from 'react-router-dom';
import Layout from '../../components/layout/Layout';
import api from '../../api/client';
import { loadingState, userInfoState } from '../../recoil/atoms';

const GROUP_META = {
  sms: { title: 'پیامک — کاوه‌نگار', color: '#1565c0' },
  enamad: { title: 'نماد اعتماد — اینماد', color: '#2e7d32' },
  payment: { title: 'درگاه پرداخت — شاپرک (سپ)', color: '#6a1b9a' },
  contact: { title: 'اطلاعات تماس', color: '#ef6c00' },
  general: { title: 'عمومی', color: '#455a64' },
};

// زرینپال فعال نیست (فقط درگاه شاپرک/سپ استفاده می‌شود) — فیلدهایش در UI نمایش داده نمی‌شود
const isHiddenKey = (key) => key.startsWith('payment.zarinpal');

const SiteSettingsPage = () => {
  const setLoading = useSetRecoilState(loadingState);
  const navigate = useNavigate();
  const userInfo = useRecoilValue(userInfoState);
  const [rows, setRows] = useState([]);
  const [values, setValues] = useState({});
  const [showSecret, setShowSecret] = useState({});
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');
  const [exitOpen, setExitOpen] = useState(false);
  // تست پیامک / درگاه — یک‌باره با کلیدهای فعلی؛ هیچ حالت تستی ماندگار نمیماند
  const [smsTest, setSmsTest] = useState({ loading: false, ok: null, msg: '' });
  const [gwTest, setGwTest] = useState({ loading: false, ok: null, msg: '' });
  const [testMobile, setTestMobile] = useState(
    userInfo && userInfo.mobile ? '0' + userInfo.mobile : ''
  );

  // آیا تغییر ذخیره‌نشده‌ای وجود دارد؟
  const isDirty = rows.some((r) => values[r.key] !== r.value);

  const handleExitClick = () => {
    if (isDirty) {
      setExitOpen(true);
    } else {
      navigate('/');
    }
  };

  const handleExitConfirm = () => {
    setExitOpen(false);
    navigate('/');
  };

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await api.get('users/site-settings/');
      setRows(res.data);
      const v = {};
      res.data.forEach((r) => {
        v[r.key] = r.value;
      });
      setValues(v);
    } catch (e) {
      setError('خطا در دریافت تنظیمات');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, []);

  const handleChange = (key, val) => {
    setValues((prev) => ({ ...prev, [key]: val }));
  };

  const handleSave = async () => {
    setLoading(true);
    setError('');
    try {
      const changed = {};
      rows.forEach((r) => {
        if (values[r.key] !== r.value) {
          changed[r.key] = values[r.key];
        }
      });
      if (Object.keys(changed).length > 0) {
        const res = await api.put('users/site-settings/', changed);
        if (res.data && res.data.settings) {
          setRows(res.data.settings);
        }
      }
      setSaved(true);
    } catch (e) {
      setError('ذخیرهٔ تنظیمات ناموفق بود؛ دوباره تلاش کنید');
    } finally {
      setLoading(false);
    }
  };

  const groups = [...new Set(rows.map((r) => r.group))];

  // ───── تست یک‌بارهٔ پیامک با کلیدهای ذخیره‌شده ─────
  const runSmsTest = async () => {
    if (isDirty) {
      setSmsTest({ loading: false, ok: false, msg: 'ابتدا تغییرات را ذخیره کنید، بعد تست بگیرید.' });
      return;
    }
    setSmsTest({ loading: true, ok: null, msg: '' });
    try {
      const res = await api.post('users/site-settings/test-sms/', { mobile: testMobile || undefined });
      setSmsTest({ loading: false, ok: !!res.data.success, msg: res.data.success ? `پیامک تست به ${res.data.to} ارسال شد ✓` : `ناموفق: ${res.data.detail || res.data.message}` });
    } catch (e) {
      setSmsTest({ loading: false, ok: false, msg: e?.response?.data?.detail || 'خطا در تست پیامک' });
    }
  };

  // ───── تست یک‌بارهٔ اتصال به درگاه (درخواست توکن واقعی) ─────
  const runGatewayTest = async () => {
    if (isDirty) {
      setGwTest({ loading: false, ok: false, msg: 'ابتدا تغییرات را ذخیره کنید، بعد تست بگیرید.' });
      return;
    }
    setGwTest({ loading: true, ok: null, msg: '' });
    try {
      const res = await api.post('users/site-settings/test-gateway/');
      setGwTest({ loading: false, ok: !!res.data.success, msg: res.data.detail });
    } catch (e) {
      setGwTest({ loading: false, ok: false, msg: e?.response?.data?.detail || 'خطا در تست درگاه' });
    }
  };

  return (
    <Layout>
      <div style={{ padding: 24, maxWidth: 1100, margin: '0 auto' }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" mb={3}>
        <Typography variant="h5" style={{ fontWeight: 'bold' }}>
          ⚙ تنظیمات سامانه
        </Typography>
        <Box display="flex" gap={1} flexWrap="wrap">
          <Button
            variant="outlined"
            color="secondary"
            startIcon={<ExitToAppIcon />}
            onClick={handleExitClick}
            style={{ fontWeight: 'bold' }}
          >
            خروج بدون ذخیره
          </Button>
          <Button
            variant="contained"
            color="primary"
            startIcon={<SaveIcon />}
            onClick={handleSave}
            style={{ fontWeight: 'bold' }}
          >
            ذخیرهٔ تغییرات
          </Button>
        </Box>
      </Box>

      <Typography variant="body2" color="textSecondary" mb={2}>
        تغییرات بلافاصله اعمال می‌شود. فیلدهای محرمانه (کلیدها) فقط برای ادمین نمایش داده می‌شوند.
      </Typography>

      {groups.map((g) => {
        const meta = GROUP_META[g] || GROUP_META.general;
        const items = rows.filter((r) => r.group === g && !isHiddenKey(r.key));
        if (!items.length) return null;
        return (
          <Card key={g} style={{ marginBottom: 20, borderRadius: 12, overflow: 'hidden' }}>
            <CardHeader
              title={
                <Typography style={{ fontWeight: 'bold', color: meta.color }}>
                  {meta.title}
                </Typography>
              }
              style={{ borderBottom: '1px solid #eee', padding: '14px 20px' }}
            />
            <CardContent style={{ padding: 20 }}>
              <Grid container spacing={2}>
                {items.map((r) => (
                  <Grid item xs={12} md={6} key={r.key}>
                    <TextField
                      fullWidth
                      size="small"
                      variant="outlined"
                      label={r.label || r.key}
                      value={values[r.key] ?? ''}
                      onChange={(e) => handleChange(r.key, e.target.value)}
                      type={r.is_secret && !showSecret[r.key] ? 'password' : 'text'}
                      InputProps={{
                        endAdornment: r.is_secret ? (
                          <InputAdornment position="end">
                            <IconButton
                              size="small"
                              aria-label="نمایش مقدار"
                              onClick={() =>
                                setShowSecret((p) => ({ ...p, [r.key]: !p[r.key] }))
                              }
                            >
                              {showSecret[r.key] ? (
                                <VisibilityOff fontSize="small" />
                              ) : (
                                <Visibility fontSize="small" />
                              )}
                            </IconButton>
                          </InputAdornment>
                        ) : undefined,
                      }}
                      helperText={
                        <span dir="ltr" style={{ fontFamily: 'monospace', fontSize: 11 }}>
                          {r.key}
                        </span>
                      }
                    />
                  </Grid>
                ))}

                {/* دکمهٔ تست یک‌باره — بعد از تست، سامانه در حالت واقعی میماند */}
                {g === 'sms' && (
                  <Grid item xs={12}>
                    <Box display="flex" alignItems="center" gap={1.5} flexWrap="wrap" style={{ marginTop: 4 }}>
                      <Button
                        variant="outlined"
                        size="small"
                        disabled={smsTest.loading}
                        onClick={runSmsTest}
                        style={{ fontWeight: 700 }}
                      >
                        {smsTest.loading ? 'در حال تست...' : '📡 ارسال پیامک تست'}
                      </Button>
                      <TextField
                        size="small"
                        variant="outlined"
                        placeholder="شمارهٔ تست (خالی = موبایل خودتان)"
                        value={testMobile}
                        onChange={(e) => setTestMobile(e.target.value)}
                        dir="ltr"
                        style={{ width: 240 }}
                        helperText={smsTest.msg ? '' : 'پیامک واقعی با کلید فعلی ارسال میشود'}
                      />
                      {smsTest.msg && (
                        <Typography style={{ fontSize: 12.5, fontWeight: 700, color: smsTest.ok ? '#2e7d32' : '#c62828' }}>
                          {smsTest.msg}
                        </Typography>
                      )}
                    </Box>
                  </Grid>
                )}
                {g === 'payment' && (
                  <Grid item xs={12}>
                    <Box display="flex" alignItems="center" gap={1.5} flexWrap="wrap" style={{ marginTop: 4 }}>
                      <Button
                        variant="outlined"
                        size="small"
                        disabled={gwTest.loading}
                        onClick={runGatewayTest}
                        style={{ fontWeight: 700 }}
                      >
                        {gwTest.loading ? 'در حال تست...' : '🔌 تست اتصال به درگاه'}
                      </Button>
                      {gwTest.msg && (
                        <Typography style={{ fontSize: 12.5, fontWeight: 700, color: gwTest.ok ? '#2e7d32' : '#c62828' }}>
                          {gwTest.msg}
                        </Typography>
                      )}
                    </Box>
                  </Grid>
                )}
              </Grid>
            </CardContent>
          </Card>
        );
      })}

      {/* هشدار خروج در صورت وجود تغییر ذخیره‌نشده */}
      <Dialog open={exitOpen} onClose={() => setExitOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle style={{ textAlign: 'right', fontWeight: 'bold' }}>
          خروج بدون ذخیره؟
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" style={{ color: '#666', lineHeight: 2.2 }}>
            تغییراتی دارید که هنوز ذخیره نشده‌اند. اگر الان خارج شوید، این تغییرات از بین می‌روند.
          </Typography>
        </DialogContent>
        <DialogActions style={{ padding: '10px 20px 18px' }}>
          <Button onClick={() => setExitOpen(false)} variant="outlined" style={{ fontWeight: 'bold' }}>
            بازگشت و ادامه ویرایش
          </Button>
          <Button
            onClick={handleExitConfirm}
            variant="contained"
            color="secondary"
            startIcon={<CloseIcon />}
            style={{ fontWeight: 'bold' }}
          >
            خروج بدون ذخیره
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={saved} autoHideDuration={4000} onClose={() => setSaved(false)}>
        <MuiAlert severity="success" variant="filled" onClose={() => setSaved(false)}>
          تنظیمات با موفقیت ذخیره شد ✓
        </MuiAlert>
      </Snackbar>
      <Snackbar open={!!error} autoHideDuration={5000} onClose={() => setError('')}>
        <MuiAlert severity="error" variant="filled" onClose={() => setError('')}>
          {error}
        </MuiAlert>
      </Snackbar>
    </div>
    </Layout>
  );
};

export default SiteSettingsPage;
